"use client";

import { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Event, FestivalSummary } from "@/lib/types";
import { formatDate, groupRecurringEvents } from "@/lib/utils";
import { EVENTS_PAGE_SIZE, getEventFeedItems, hasCardImagery, hasPlayableAct, type EventFeedItem } from "@/lib/event-feed";
import { EventCard } from "./event-card";
import { EventCardList } from "./event-card-list";
import { FestivalCard } from "./festival-card";
import { FestivalCardList } from "./festival-card-list";
import { EventsFeedSkeleton } from "./events-feed-skeleton";
import type { InitialEventImage } from "@/lib/initial-event-image";

const PAGE_SIZE = EVENTS_PAGE_SIZE;
// List view: playable compact (no-image) rows shown per day before "show more"
const COMPACT_PREVIEW = 5;

/* Each day folds two tails behind a button: "compact" is the playable
   compact rows past the preview, "audio" is every bill with nothing to
   preview, which the feed sorts to the end of the day. */
type ShowMoreKind = "compact" | "audio";
type ListItem =
  | EventFeedItem
  | { type: "showMore"; kind: ShowMoreKind; sortDate: string; hiddenCount: number };
const expandKey = (kind: ShowMoreKind, day: string) => `${kind}:${day}`;

interface EventsGridProps {
  events: Event[];
  initialImage?: InitialEventImage;
  festivals?: FestivalSummary[];
  blockedSpotifyIds: string[];
  citySlug?: string;
  preferredArtistId?: number;
  showCity?: boolean;
  view?: "list" | "grid";
  fillWidth?: boolean;
  groupShowCounts?: Record<string, number>;
  /** Rendered at the right end of the first date header row */
  headerTrailing?: ReactNode;
  /** Show date group headers in grid view too */
  gridDateHeaders?: boolean;
  /** Hide events without a featured image in grid view */
  gridImageOnly?: boolean;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  /** The route supplies one complete page and crawlable pagination links. */
  externalPagination?: boolean;
}

export function EventsGrid({ events, initialImage, festivals = [], blockedSpotifyIds, citySlug, preferredArtistId, showCity, view = "grid", fillWidth, groupShowCounts, headerTrailing, gridDateHeaders, gridImageOnly, isLoading = false, isLoadingMore = false, externalPagination = false }: EventsGridProps) {
  const locale = useLocale();
  const t = useTranslations("Events");
  const rawGroups = useMemo(() => groupRecurringEvents(events), [events]);
  const groups = useMemo(() => {
    if (!groupShowCounts) return rawGroups;
    return rawGroups.map((g) => {
      const total = groupShowCounts[g.key];
      return total && total > g.events.length ? { ...g, totalShowCount: total } : g;
    });
  }, [rawGroups, groupShowCounts]);

  // Interleave festivals with event groups by date
  const items = useMemo(
    () => getEventFeedItems(groups, festivals, view, gridImageOnly, blockedSpotifyIds),
    [groups, festivals, view, gridImageOnly, blockedSpotifyIds],
  );

  // Per-day sections that are fully expanded (list view), keyed by expandKey
  const [expandedDays, setExpandedDays] = useState<Set<string>>(() => new Set());

  // List view: each day leads with its playable bills (image cards, then up
  // to COMPACT_PREVIEW compact rows, the rest behind "show more"), then
  // folds every bill without audio behind a second button. Festivals are
  // never collapsed.
  const listItems = useMemo((): ListItem[] => {
    if (view !== "list" || externalPagination) return items;
    const out: ListItem[] = [];
    let day = "";
    let compactSeen = 0;
    let hiddenCompact = 0;
    let hiddenSilent = 0;
    // The compact button goes out as soon as the day's playable run ends, so
    // it sits above the no-audio bills whether or not those are expanded.
    const flushCompact = () => {
      if (hiddenCompact > 0) out.push({ type: "showMore", kind: "compact", sortDate: day, hiddenCount: hiddenCompact });
      hiddenCompact = 0;
    };
    const flushDay = () => {
      flushCompact();
      if (hiddenSilent > 0) out.push({ type: "showMore", kind: "audio", sortDate: day, hiddenCount: hiddenSilent });
      hiddenSilent = 0;
      compactSeen = 0;
    };
    for (const item of items) {
      if (item.sortDate !== day) {
        flushDay();
        day = item.sortDate;
      }
      if (item.type === "event") {
        const event = item.group.events[0];
        if (!hasPlayableAct(event, blockedSpotifyIds)) {
          flushCompact();
          if (!expandedDays.has(expandKey("audio", day))) {
            hiddenSilent += 1;
            continue;
          }
        } else if (!hasCardImagery(event) && !expandedDays.has(expandKey("compact", day))) {
          compactSeen += 1;
          if (compactSeen > COMPACT_PREVIEW) {
            hiddenCompact += 1;
            continue;
          }
        }
      }
      out.push(item);
    }
    flushDay();
    return out;
  }, [items, view, expandedDays, externalPagination, blockedSpotifyIds]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Reset visible count and expanded days when events shrink (filter
  // applied), but not when they grow (lazy-load)
  const prevEventCount = useRef(events.length);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisibleCount((prev) =>
      events.length < prev ? PAGE_SIZE : prev
    );
    if (events.length < prevEventCount.current) setExpandedDays(new Set());
    prevEventCount.current = events.length;
  }, [events]);

  const hasMore = !externalPagination && visibleCount < listItems.length;

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, listItems.length));
  }, [listItems.length]);

  // Expanding a day grows the window by the revealed rows so nothing already
  // on screen below it gets pushed past the pagination cutoff.
  const expandDay = useCallback((kind: ShowMoreKind, day: string, hiddenCount: number) => {
    setExpandedDays((prev) => new Set(prev).add(expandKey(kind, day)));
    setVisibleCount((prev) => prev + hiddenCount);
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "400px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, isLoading]);

  if (isLoading) {
    return <EventsFeedSkeleton view={view} fillWidth={fillWidth} headerTrailing={headerTrailing} />;
  }

  if (events.length === 0 && festivals.length === 0 && !isLoadingMore) {
    return (
      <>
        {headerTrailing && (
          <div className="flex justify-end">{headerTrailing}</div>
        )}
        <div className="flex flex-col items-center justify-center py-24">
          <p className="font-mono text-sm text-neutral-500">
            NO SHOWS MATCH YOUR FILTERS
          </p>
          <p className="mt-2 font-mono text-xs text-neutral-700">
            TRY ADJUSTING YOUR SEARCH CRITERIA
          </p>
        </div>
      </>
    );
  }

  const visibleItems = externalPagination ? listItems : listItems.slice(0, visibleCount);
  // The first photo is the mobile list's LCP element. Compact rows and
  // festivals can precede it, so use the first image card rather than row 0.
  const firstImageIndex = visibleItems.findIndex(
    (item) => item.type === "event" && hasCardImagery(item.group.events[0]),
  );

  const labelFor = (sortDate: string) =>
    sortDate === "9999-99-99" ? t("dateTba") : formatDate(sortDate, locale, t("dateTba"));

  return (
    <>
      {view === "list" ? (
        <div className={`grid grid-cols-1 gap-4 ${fillWidth ? "" : "mx-auto max-w-3xl sm:pt-4 lg:max-w-5xl"}`}>
          {visibleItems.map((item, i) => {
            const key =
              item.type === "festival"
                ? `festival-${item.festival.id}`
                : item.type === "showMore"
                  ? `show-more-${item.kind}-${item.sortDate}`
                  : item.group.key;
            // Date group header whenever the (sorted) date changes
            const showHeader =
              i === 0 || item.sortDate !== visibleItems[i - 1].sortDate;
            return (
              <Fragment key={key}>
                {showHeader && (
                  <div className="mt-8 flex items-center gap-4 pl-2 first:mt-0 lg:pl-0">
                    <span className="font-mono text-base font-bold uppercase tracking-widest text-acid">
                      {labelFor(item.sortDate)}
                    </span>
                    <div className="h-px flex-1 bg-neutral-700/70" />
                    {i === 0 && headerTrailing}
                  </div>
                )}
                {item.type === "showMore" ? (
                  <button
                    type="button"
                    onClick={() => expandDay(item.kind, item.sortDate, item.hiddenCount)}
                    className="w-full cursor-pointer rounded-lg border border-neutral-800 px-4 py-3 font-mono text-xs uppercase tracking-widest text-neutral-400 transition-colors hover:border-neutral-600 hover:text-acid"
                  >
                    {t(item.kind === "audio" ? "showMoreWithoutAudio" : "showMoreForDay", { count: item.hiddenCount })}
                  </button>
                ) : item.type === "festival" ? (
                  <FestivalCardList
                    festival={item.festival}
                    citySlug={citySlug ?? ""}
                  />
                ) : (
                  <EventCardList
                    event={item.group.events[0]}
                    eventGroup={item.group}
                    blockedSpotifyIds={blockedSpotifyIds}
                    citySlug={citySlug}
                    showCity={showCity}
                    prioritizeImage={i === firstImageIndex}
                    initialImage={i === firstImageIndex ? initialImage : undefined}
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {visibleItems.map((item, i) => {
            if (item.type === "showMore") return null;
            const key =
              item.type === "festival"
                ? `festival-${item.festival.id}`
                : item.group.key;
            const showHeader =
              gridDateHeaders &&
              (i === 0 || item.sortDate !== visibleItems[i - 1].sortDate);
            return (
              <Fragment key={key}>
                {showHeader && (
                  <div className="col-span-full mt-4 flex items-center gap-4 pl-2 first:mt-0 lg:pl-0">
                    <span className="font-mono text-base font-bold uppercase tracking-widest text-acid">
                      {labelFor(item.sortDate)}
                    </span>
                    <div className="h-px flex-1 bg-neutral-700/70" />
                    {i === 0 && headerTrailing}
                  </div>
                )}
                {item.type === "festival" ? (
                  <FestivalCard
                    festival={item.festival}
                    citySlug={citySlug ?? ""}
                  />
                ) : (
                  <EventCard
                    event={item.group.events[0]}
                    eventGroup={item.group}
                    blockedSpotifyIds={blockedSpotifyIds}
                    citySlug={citySlug}
                    preferredArtistId={preferredArtistId}
                    showCity={showCity}
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      )}

      {isLoadingMore && (
        <div className="mt-4">
          <EventsFeedSkeleton view={view} fillWidth={fillWidth} showDateHeader={visibleItems.length === 0} headerTrailing={headerTrailing} />
        </div>
      )}

      {hasMore && (
        <div
          ref={sentinelRef}
          className="flex justify-center py-12"
        >
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-700 border-t-acid" />
        </div>
      )}
    </>
  );
}
